/**
 * Order validation logic for the prediction market simulation.
 * All orders must pass validation before being processed by the matching engine.
 */

import type { NewOrder, OrderValidation, OrderValidationContext } from './types'

/**
 * Validates an order before it can be processed.
 *
 * Validation checks:
 * 1. Price bounds (0.01 to 0.99) for limit orders
 * 2. Quantity must be a positive integer
 * 3. User must have sufficient balance for worst-case cost
 *
 * @param order - The order to validate
 * @param context - The user's current available trading resources
 * @returns Validation result with isValid flag and optional rejection reason
 */
export function validateOrder(
  order: NewOrder,
  context: OrderValidationContext
): OrderValidation {
  // Price validation (limit orders only)
  if (order.type === 'limit') {
    if (order.price < 0.01 || order.price > 0.99) {
      return { isValid: false, rejectionReason: 'invalid-price' }
    }
  }

  // Quantity validation - must be a positive integer
  if (!Number.isInteger(order.quantity) || order.quantity <= 0) {
    return { isValid: false, rejectionReason: 'invalid-quantity' }
  }

  if (order.side === 'buy') {
    // For limit orders: max cost is quantity * price
    // For market orders: max cost is quantity * 1.0 (worst case - buying at $1.00)
    const maxCost = order.quantity * (order.type === 'limit' ? order.price : 1.0)
    if (maxCost > context.balance) {
      return { isValid: false, rejectionReason: 'insufficient-balance' }
    }
  } else if (order.quantity > context.availableShares) {
    return { isValid: false, rejectionReason: 'insufficient-shares' }
  }

  return { isValid: true }
}
